import {
  AutocompleteGenericEntity,
  FetchAutocomplete,
  FetchAutocompleteProps,
} from '@australiangreens/ag-internal-components';
import { useMemo, useState } from 'react';

import { FieldMetaData } from './types';

type FetchAutocompleteByLabelTFCProps<T extends AutocompleteGenericEntity> = Omit<
  FetchAutocompleteProps<T>,
  'value' | 'onChange'
> & {
  onChange: (value: string[], fieldMetaData: FieldMetaData) => void;
  isEmptyValid?: boolean;
  /** Labels for Restart pre-fill (matched from preLoadedOptions by label) */
  initialValue?: string[];
};

/** Covers all the simple filters fields that are an array of numbers used in a
 *  FetchAutocomplete. Its simply a wrapper around FetchAutocomplete to manage
 *  the state and pass back a value via onChange. This simplifies the parent.
 */
export default function FetchAutocompleteByLabelTFC<T extends AutocompleteGenericEntity>({
  isEmptyValid = false,
  onChange,
  initialValue,
  preLoadedOptions,
  ...props
}: FetchAutocompleteByLabelTFCProps<T>) {
  const [internalValue, setInternalValue] = useState<T[]>([]);
  const [touched, setTouched] = useState(false);

  const value = useMemo(() => {
    if (!touched && internalValue.length === 0 && initialValue?.length && preLoadedOptions?.length) {
      return preLoadedOptions.filter((o) => initialValue.includes(o.label)) as T[];
    }
    return internalValue;
  }, [touched, internalValue, initialValue, preLoadedOptions]);

  const handleOnChange = (newValue: T[]) => {
    setTouched(true);
    setInternalValue(newValue);

    const state = newValue.length > 0 ? 'valid' : isEmptyValid ? 'valid' : 'empty';

    onChange(
      newValue.map((x) => x.label),
      { state, error: null }
    );
  };

  return (
    <FetchAutocomplete
      textFieldColor="secondary"
      onChange={handleOnChange}
      value={value}
      preLoadedOptions={preLoadedOptions}
      {...props}
    />
  );
}
